/**
 * Department vocabulary / glossary (D2, workspace docs/guardian/03 §3.2,
 * 2026-10-22): the codes and local terms Guardian should understand and, in
 * plain-language mode, translate. Admins import a code list, add terms, and
 * approve/reject/retire each one; members see the approved glossary read-only.
 * All term text comes from the Backend and is rendered as text only. A 503
 * `terms_unavailable` hides the section (like DepartmentRules on rules_unavailable).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  addDepartmentTerm,
  approveDepartmentTerm,
  fetchDepartmentTerms,
  importDepartmentTerms,
  rejectDepartmentTerm,
  retireDepartmentTerm,
} from '../api/guardianApi';
import { departmentErrorMessage } from '../utils/departmentErrors';
import { flagText } from './RuleCard';

const PAGE = 50;
// The Backend caps the CSV at 256 KB; check before sending so an oversize file
// is refused in the browser with a clear message (external bytes are bounded).
export const MAX_CSV_BYTES = 256 * 1024;

export const SCOPES = [
  ['both', 'Radio and written'],
  ['radio', 'Radio only'],
  ['written', 'Written only'],
];

export function scopeLabel(scope) {
  return (SCOPES.find(([v]) => v === scope) || [null, scope])[1];
}

const SOURCE_TEXT = {
  csv: 'From a code list',
  rule: 'From a document',
  manual: 'Added by your team',
};

function AddTerm({ onAdd }) {
  const [surface, setSurface] = useState('');
  const [meaning, setMeaning] = useState('');
  const [scope, setScope] = useState('both');
  const [internalOnly, setInternalOnly] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const save = async () => {
    setBusy(true);
    setError(null);
    const err = await onAdd({
      surface: surface.trim(),
      meaning: meaning.trim(),
      scope,
      internalOnly,
    });
    setBusy(false);
    if (err) {
      setError(err);
      return;
    }
    setSurface('');
    setMeaning('');
    setScope('both');
    setInternalOnly(true);
  };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <TextField size="small" label="Code or term" value={surface} onChange={(e) => setSurface(e.target.value)} inputProps={{ maxLength: 120 }} />
        <TextField size="small" label="Plain meaning" value={meaning} onChange={(e) => setMeaning(e.target.value)} inputProps={{ maxLength: 300 }} sx={{ flexGrow: 1, minWidth: 220 }} />
        <Select size="small" value={scope} onChange={(e) => setScope(e.target.value)} inputProps={{ 'aria-label': 'Where it is used' }}>
          {SCOPES.map(([v, label]) => <MenuItem key={v} value={v}>{label}</MenuItem>)}
        </Select>
        <FormControlLabel
          control={<Switch size="small" checked={internalOnly} onChange={(e) => setInternalOnly(e.target.checked)} />}
          label="Internal only"
        />
        <Button variant="contained" onClick={save} disabled={busy || !surface.trim() || !meaning.trim()}>Add term</Button>
      </Box>
    </Box>
  );
}

function ImportCsv({ onImport }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const tooBig = Boolean(file && file.size > MAX_CSV_BYTES);
  const run = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const csv = await file.text();
      if (csv.length > MAX_CSV_BYTES) {
        setError('That code list is too large. The limit is 256 KB.');
        return;
      }
      const res = await onImport(csv);
      if (res.error) {
        setError(res.error);
        return;
      }
      setResult(res.data);
      setFile(null);
    } catch {
      setError('The file could not be read. Try saving it as CSV again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="body2" color="text.secondary">
        Import a code list as a CSV with columns <code>code,meaning</code> (an optional third column sets
        where it is used: radio, written or both). Imported terms start as drafts for review.
      </Typography>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {result && (
        <Alert severity="success" onClose={() => setResult(null)}>
          {`Added ${result.created ?? 0} new, skipped ${result.skipped ?? 0} already present, `
            + `${result.invalid ?? 0} could not be read.`}
        </Alert>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <Button component="label" variant="outlined" disabled={busy}>
          {file ? file.name : 'Choose CSV'}
          <input
            hidden
            type="file"
            accept=".csv,text/csv"
            aria-label="Code list CSV"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); setError(null); }}
          />
        </Button>
        <Button variant="contained" onClick={run} disabled={!file || tooBig || busy}>
          {busy ? 'Importing…' : 'Import'}
        </Button>
      </Box>
      {tooBig && <Typography variant="caption" color="error">That file is over the 256 KB limit.</Typography>}
    </Box>
  );
}

function TermRow({ term, isAdmin, onApprove, onReject, onRetire }) {
  const flagged = (term.flags || []).length > 0;
  return (
    <Paper variant="outlined" component="li" sx={{ p: 1.5, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>{term.surface}</Typography>
        <Chip size="small" variant="outlined" label={scopeLabel(term.scope)} />
        {term.internalOnly && <Chip size="small" variant="outlined" label="Internal only" />}
        {SOURCE_TEXT[term.source] && <Chip size="small" variant="outlined" label={SOURCE_TEXT[term.source]} />}
        {flagged && <Chip size="small" color="warning" label="Check wording" />}
      </Box>
      <Typography variant="body2">{term.meaning}</Typography>
      {Array.isArray(term.aliases) && term.aliases.length > 0 && (
        <Typography variant="caption" color="text.secondary">Also: {term.aliases.join(', ')}</Typography>
      )}
      {term.canonicalId && (
        <Typography variant="caption" color="text.secondary">Standard code: {term.canonicalId}</Typography>
      )}
      {flagged && (
        <Alert severity="warning">
          This text {flagText(term.flags)}. Make sure it is really your department&apos;s term before approving.
        </Alert>
      )}
      {isAdmin && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          {term.status === 'draft' && (
            <>
              <Button size="small" variant="contained" onClick={() => onApprove(term)}>Approve</Button>
              <Button size="small" color="error" onClick={() => onReject(term)}>Reject</Button>
            </>
          )}
          {term.status === 'approved' && (
            <Button size="small" color="error" onClick={() => onRetire(term)}>Retire</Button>
          )}
        </Box>
      )}
    </Paper>
  );
}

/**
 * @param {object} props
 * @param {string|null} props.authToken
 * @param {boolean} [props.isAdmin]
 */
export default function DepartmentVocabulary({ authToken, isAdmin = false }) {
  const [tab, setTab] = useState(isAdmin ? 'draft' : 'approved');
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [list, setList] = useState({ total: 0, terms: [] });
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState(null);
  const [off, setOff] = useState(false);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const load = useCallback(async () => {
    const res = await fetchDepartmentTerms(authToken, {
      status: isAdmin ? tab : 'approved', q: query || undefined, offset, limit: PAGE,
    });
    if (!alive.current) return;
    if (res?.success) setList({ total: res.data?.total || 0, terms: res.data?.terms || [] });
    else if (res?.data?.code === 'terms_unavailable') setOff(true);
    else setError(departmentErrorMessage(res, 'Could not load the glossary.'));
  }, [authToken, isAdmin, tab, query, offset]);
  useEffect(() => { load(); }, [load]);

  const act = async (call, fallback) => {
    setError(null);
    const res = await call();
    if (!res?.success) {
      const msg = departmentErrorMessage(res, fallback);
      setError(msg);
      load();
      return msg;
    }
    load();
    return null;
  };

  const onAdd = (body) => act(() => addDepartmentTerm(authToken, body), 'Could not add the term.');
  const onImport = async (csv) => {
    const res = await importDepartmentTerms(authToken, csv);
    if (res?.success) {
      load();
      return { data: res.data || {} };
    }
    return { error: departmentErrorMessage(res, 'Could not import the code list.') };
  };
  const onApprove = (t) => act(() => approveDepartmentTerm(authToken, t.id, t.rev), 'Could not approve the term.');
  const onReject = (t) => act(() => rejectDepartmentTerm(authToken, t.id, t.rev), 'Could not reject the term.');
  const onRetire = (t) => act(() => retireDepartmentTerm(authToken, t.id, t.rev), 'Could not retire the term.');

  // Switched off for this deployment: show nothing.
  if (off) return null;

  const runSearch = () => { setQuery(q.trim()); setOffset(0); };

  return (
    <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="h6" component="h2">Vocabulary</Typography>
      <Typography variant="body2" color="text.secondary">
        {isAdmin
          ? 'The codes and local terms Guardian should understand. In plain-language mode it uses these meanings instead of codes.'
          : 'Your department’s approved codes and terms, with their plain meanings.'}
      </Typography>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {isAdmin && (
        <>
          <ImportCsv onImport={onImport} />
          <AddTerm onAdd={onAdd} />
          <Tabs value={tab} onChange={(_, v) => { setTab(v); setOffset(0); }} aria-label="Vocabulary views" variant="scrollable">
            <Tab value="draft" label="To review" />
            <Tab value="approved" label="Approved" />
            <Tab value="rejected" label="Rejected" />
            <Tab value="retired" label="Retired" />
          </Tabs>
        </>
      )}

      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          size="small"
          fullWidth
          label="Search the glossary"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
          inputProps={{ maxLength: 120 }}
        />
        <Button onClick={runSearch}>Search</Button>
      </Box>

      <Box component="ul" sx={{ m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {list.terms.map((t) => (
          <TermRow key={`${t.id}-${t.rev}`} term={t} isAdmin={isAdmin} onApprove={onApprove} onReject={onReject} onRetire={onRetire} />
        ))}
      </Box>
      {list.terms.length === 0 && (
        <Typography variant="body2" color="text.secondary">No terms here yet.</Typography>
      )}
      {list.total > PAGE && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button size="small" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Previous</Button>
          <Typography variant="caption">{offset + 1}–{Math.min(offset + PAGE, list.total)} of {list.total}</Typography>
          <Button size="small" disabled={offset + PAGE >= list.total} onClick={() => setOffset(offset + PAGE)}>Next</Button>
        </Box>
      )}
    </Paper>
  );
}
